import React from 'react';
import ReactDOM from 'react-dom';
const { Icon, Card, Button } = window;
/* ============================================================
   BOOKER — Tela: Certificados digitais (importação + monitoramento)
   ============================================================ */
var { useState, useEffect, useMemo } = React;

const fmtData = (iso) => (iso ? window.DB.fmtData(iso) : '—');
const MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const mesKey = (iso) => { const x = window.DB.parseData(iso); return isNaN(x.getTime()) ? '' : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
const mesLabel = (key) => { const [y, m] = key.split('-'); return `${MES[+m - 1]}/${y}`; };

function statusDias(dias) {
  if (dias == null) return { txt: 'Sem data', cor: 'var(--text-faint)', bg: 'var(--surface-inset)' };
  if (dias < 0) return { txt: `Vencido há ${Math.abs(dias)}d`, cor: 'var(--danger)', bg: 'var(--danger-bg)' };
  if (dias <= 30) return { txt: `Vence em ${dias}d`, cor: 'var(--danger)', bg: 'var(--danger-bg)' };
  if (dias <= 90) return { txt: `${dias}d`, cor: 'var(--booker-orange)', bg: 'var(--warn-bg)' };
  return { txt: `${dias}d`, cor: 'var(--ok)', bg: 'var(--info-bg)' };
}

// Coluna "Válido por": mostra a DATA de validade (verde/laranja/vermelho pela urgência).
function validade(venc, dias) {
  if (!venc || dias == null) return { txt: '—', cor: 'var(--text-faint)', bg: 'var(--surface-inset)' };
  const d = fmtData(venc);
  if (dias < 0) return { txt: `Venceu em ${d}`, cor: 'var(--danger)', bg: 'var(--danger-bg)' };
  if (dias <= 90) return { txt: `Válido até ${d}`, cor: 'var(--booker-orange)', bg: 'var(--warn-bg)' };
  return { txt: `Válido até ${d}`, cor: 'var(--ok)', bg: 'var(--info-bg)' };
}

function Certificados() {
  const [estado, setEstado] = useState('carregando');
  const [lista, setLista] = useState([]);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [fMes, setFMes] = useState('all');
  const [fGrupo, setFGrupo] = useState('all');
  const [sincronizando, setSincronizando] = useState(false);
  const [pastaUrl, setPastaUrl] = useState('');
  const [arquivoSp, setArquivoSp] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const podeEditar = window.podeEditar ? window.podeEditar() : true;

  async function carregar() {
    setEstado('carregando'); setErro('');
    try { setLista(await window.Api.apiCertificados()); setEstado('ok'); }
    catch (e) { setErro(e.message || 'Falha ao carregar'); setEstado('erro'); }
  }
  useEffect(() => { carregar(); }, []);

  useEffect(() => {
    window.Api.apiCertificadosSharepoint()
      .then(d => { setPastaUrl((d && d.pastaUrl) || ''); setArquivoSp((d && d.arquivo) || ''); })
      .catch(() => {});
  }, []);

  // Puxa a aba BASE CERTIFICADOS da planilha-mestre (o robô também faz sozinho).
  async function sincronizar() {
    setSincronizando(true);
    try {
      const r = await window.Api.apiCertificadosSincronizar();
      window.avisar(`Sincronizado de "${r.arquivo}": ${r.inseridos} novo(s) · ${r.atualizados} atualizado(s) · ${r.removidos || 0} removido(s). O banco reflete a planilha.`);
      carregar();
    } catch (e) { window.avisar(e.message || 'Falha ao sincronizar.'); }
    finally { setSincronizando(false); }
  }

  async function excluir(c) {
    if (!await window.confirmar(`Excluir o certificado de ${c.razao || c.cnpj}?`)) return;
    try { await window.Api.apiExcluirCertificado(c.id); carregar(); }
    catch (e) { window.avisar(e.message); }
  }

  // Cascata nos DOIS sentidos: cada dropdown oferece só o que existe no recorte
  // dos OUTROS filtros. `base` = só a busca (comum aos dois).
  const base = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return !q ? lista : lista.filter(c => (c.razao || '').toLowerCase().includes(q) || (c.cnpj || '').includes(q) || (c.grupo || '').toLowerCase().includes(q));
  }, [lista, busca]);

  const casaMes = (c) => fMes === 'all' || (c.vencimento && mesKey(c.vencimento) === fMes);
  const casaGrupo = (c) => fGrupo === 'all' || (c.grupo || '').trim() === fGrupo;

  // opções de GRUPO: base + filtro de mês (não filtra por grupo)
  const grupos = useMemo(
    () => [...new Set(base.filter(casaMes).map(c => (c.grupo || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [base, fMes],
  );
  // opções de MÊS (vencimento): base + filtro de grupo (não filtra por mês)
  const meses = useMemo(() => {
    const m = new Map();
    base.filter(casaGrupo).forEach(c => { if (c.vencimento) { const k = mesKey(c.vencimento); if (k) m.set(k, mesLabel(k)); } });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [base, fGrupo]);

  // se a seleção sumiu do recorte, volta para "todos"
  useEffect(() => { if (fGrupo !== 'all' && !grupos.includes(fGrupo)) setFGrupo('all'); }, [grupos, fGrupo]);
  useEffect(() => { if (fMes !== 'all' && !meses.some(([k]) => k === fMes)) setFMes('all'); }, [meses, fMes]);

  const filtrada = useMemo(
    () => base.filter(c => casaMes(c) && casaGrupo(c)),
    [base, fMes, fGrupo],
  );

  const kpis = useMemo(() => ({
    total: lista.length,
    vencidos: lista.filter(c => c.dias != null && c.dias < 0).length,
    venc30: lista.filter(c => c.dias != null && c.dias >= 0 && c.dias <= 30).length,
    venc90: lista.filter(c => c.dias != null && c.dias > 30 && c.dias <= 90).length,
  }), [lista]);

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Certificados</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Espelho da aba BASE CERTIFICADOS no SharePoint · {estado === 'ok' ? `${lista.length}` : '…'}</p>
          {pastaUrl && (
            <a href={pastaUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13, fontWeight: 700, color: 'var(--booker-orange)', textDecoration: 'none' }}>
              <Icon name="external" size={14} /> Abrir a pasta CERTIFICADO DIGITAL no SharePoint{arquivoSp ? ` · ${arquivoSp}` : ''}
            </a>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {podeEditar && <Button variant="accent" size="md" icon="refresh" onClick={sincronizando ? undefined : sincronizar} title="Puxa a aba BASE CERTIFICADOS da planilha no SharePoint">{sincronizando ? 'Sincronizando…' : 'Sincronizar do SharePoint'}</Button>}
        </div>
      </div>

      {estado === 'ok' && lista.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
          <Kpi label="Total" value={kpis.total} tone="var(--text-strong)" />
          <Kpi label="Vencidos" value={kpis.vencidos} tone="var(--danger)" />
          <Kpi label="Vencem em 30 dias" value={kpis.venc30} tone="var(--danger)" />
          <Kpi label="Vencem em 31–90 dias" value={kpis.venc90} tone="var(--booker-orange)" />
        </div>
      )}

      {estado === 'ok' && lista.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 420 }}>
            <Icon name="search" size={17} style={{ position: 'absolute', left: 13, top: 11, color: 'var(--text-muted)' }} />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por razão social, CNPJ ou grupo…"
              style={{ width: '100%', padding: '10px 12px 10px 40px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, outline: 'none' }} />
          </div>
          <select value={fGrupo} onChange={e => setFGrupo(e.target.value)} title="Filtrar por grupo de empresas"
            style={{ padding: '10px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
            <option value="all">Todos os grupos ({grupos.length})</option>
            {grupos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={fMes} onChange={e => setFMes(e.target.value)} title="Filtrar por mês de vencimento"
            style={{ padding: '10px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
            <option value="all">Todos os vencimentos ({meses.length})</option>
            {meses.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      )}

      {estado === 'carregando' && (
        <Card pad={40} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <span style={{ width: 34, height: 34, border: '4px solid var(--surface-inset)', borderTopColor: 'var(--booker-orange)', borderRadius: 99, display: 'inline-block', animation: 'spin .8s linear infinite' }} />
          <div style={{ marginTop: 14, fontSize: 14 }}>Carregando certificados…</div>
        </Card>
      )}

      {estado === 'erro' && (
        <Card pad={28} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--danger-bg)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={22} /></span>
          <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>Não foi possível carregar</div><div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>{erro}</div></div>
          <Button variant="default" onClick={carregar}>Tentar de novo</Button>
        </Card>
      )}

      {estado === 'ok' && lista.length === 0 && (
        <Card pad={40} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <Icon name="file" size={32} style={{ color: 'var(--text-faint)' }} />
          <div style={{ marginTop: 12, fontSize: 14 }}>{podeEditar ? <>Nenhum certificado ainda. Baixe o <b>Modelo</b>, preencha e clique em <b>Importar</b>.</> : 'Nenhum certificado cadastrado ainda.'}</div>
        </Card>
      )}

      {estado === 'ok' && filtrada.length > 0 && (
        <Card pad={0} style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                  <Th>Razão social</Th><Th>CNPJ</Th><Th>Certificado</Th><Th>Vencimento</Th><Th>Tipo</Th><Th>Grupo de empresas</Th><Th>Válido por</Th><Th right>Ações</Th>
                </tr>
              </thead>
              <tbody>
                {filtrada.map((c, i) => {
                  const s = validade(c.vencimento, c.dias);
                  const sit = c.situacao || (c.status || '');
                  const sitOk = sit === 'Válido';
                  const sitBad = sit === 'Vencido';
                  const divergente = /^v[áa]lido/i.test(c.status || '') && sitBad; // importado diz "válido" mas está vencido
                  return (
                    <tr key={c.id} style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{c.razao || '—'}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }} className="mono">{c.cnpj || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: sitOk ? 'var(--ok)' : (sitBad ? 'var(--danger)' : 'var(--text-faint)'), background: sitOk ? 'var(--info-bg)' : (sitBad ? 'var(--danger-bg)' : 'var(--surface-inset)'), padding: '3px 9px', borderRadius: 99 }}>{sit || '—'}</span>
                        {divergente && <div style={{ fontSize: 9.5, color: 'var(--text-faint)', marginTop: 3 }} title="O status importado estava desatualizado">importado: {c.status}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-strong)' }}>{fmtData(c.vencimento)}</td>
                      <td style={{ padding: '12px 16px' }}>{c.tipo || '—'}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{c.grupo || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: s.cor, background: s.bg, padding: '4px 9px', borderRadius: 99, whiteSpace: 'nowrap' }}>{s.txt}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button onClick={() => setDetalhe(c)} title="Ver / editar · contato, vínculo e histórico"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 99, border: '1px solid var(--border)', background: (c.obsCount || c.email || c.telefone) ? 'var(--info-bg)' : 'var(--surface)', color: (c.obsCount || c.email || c.telefone) ? 'var(--info)' : 'var(--text-muted)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            <Icon name="eye" size={15} />{c.obsCount ? c.obsCount : ''}
                          </button>
                          {podeEditar && <button onClick={() => setDetalhe(c)} title="Editar dados do certificado" style={{ width: 32, height: 32, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            <Icon name="edit" size={15} />
                          </button>}
                          {podeEditar && <button onClick={() => excluir(c)} title="Excluir" style={{ width: 32, height: 32, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            <Icon name="trash" size={15} />
                          </button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {busca && <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>{filtrada.length} de {lista.length}</div>}
        </Card>
      )}

      {estado === 'ok' && lista.length > 0 && filtrada.length === 0 && (
        <Card pad={28} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum certificado {fGrupo !== 'all' ? `no grupo ${fGrupo}` : fMes !== 'all' ? `vence em ${mesLabel(fMes)}` : `corresponde a “${busca}”`}{(fGrupo !== 'all' && fMes !== 'all') ? ` em ${mesLabel(fMes)}` : ''}.</Card>
      )}

      {detalhe && <DetalheCertificado c={detalhe} onClose={() => setDetalhe(null)} onChanged={carregar} />}
    </div>
  );
}

function DetalheCertificado({ c, onClose, onChanged }) {
  const DB = window.DB;
  const [email, setEmail] = useState(c.email || '');
  const [telefone, setTelefone] = useState(c.telefone || '');
  const [razao, setRazao] = useState(c.razao || '');
  const [cnpj, setCnpj] = useState(c.cnpj || '');
  const [status, setStatus] = useState(c.status || '');
  const [vencimento, setVencimento] = useState(c.vencimento || '');
  const [tipo, setTipo] = useState(c.tipo || '');
  const [grupo, setGrupo] = useState(c.grupo || '');
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [lista, setLista] = useState(null);
  const [texto, setTexto] = useState('');

  async function carregarObs() {
    try { setLista(await window.Api.apiCertObsList(c.id)); } catch (e) { setErro(e.message); setLista([]); }
  }
  useEffect(() => { carregarObs(); }, []);

  async function salvarContato() {
    setErro(''); setSalvando(true);
    try { await window.Api.apiEditarCertificado(c.id, { email, telefone }); if (onChanged) onChanged(); window.avisar('Contato salvo.'); }
    catch (e) { setErro(e.message); } finally { setSalvando(false); }
  }
  async function salvarDados() {
    setErro(''); setSalvandoDados(true);
    try {
      await window.Api.apiEditarCertificado(c.id, { razao_social: razao, cnpj, status, vencimento: vencimento || null, tipo, grupo });
      if (onChanged) onChanged(); window.avisar('Dados do certificado salvos.');
    } catch (e) { setErro(e.message); } finally { setSalvandoDados(false); }
  }
  async function addObs() {
    const t = texto.trim(); if (!t) return;
    setErro('');
    try { setLista(await window.Api.apiCertObsAdd(c.id, t)); setTexto(''); if (onChanged) onChanged(); }
    catch (e) { setErro(e.message); }
  }
  async function delObs(id) {
    if (!await window.confirmar('Excluir esta observação?')) return;
    try { await window.Api.apiCertObsDel(id); await carregarObs(); if (onChanged) onChanged(); } catch (e) { setErro(e.message); }
  }
  const fmtDH = (iso) => { try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };
  const campo = { width: '100%', padding: '9px 11px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)', background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, outline: 'none' };
  const Label = ({ children }) => <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{children}</label>;
  const Secao = ({ children }) => <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '4px 0 10px' }}>{children}</div>;

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,22,.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 100, animation: 'fadeIn .2s ease both', padding: '40px 20px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 580, background: 'var(--surface)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', animation: 'popIn .25s ease both', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Icon name="eye" size={19} style={{ color: 'var(--booker-orange)', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.razao || 'Certificado'}</h3>
              <div className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.cnpj || '—'}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', padding: 4 }}><Icon name="x" size={18} /></button>
        </div>

        <div style={{ padding: 22, maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Dados do certificado (corrigir campo errado da importação) */}
          <Secao>Dados do certificado</Secao>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}><Label>Razão social</Label><input style={campo} value={razao} onChange={e => setRazao(e.target.value)} /></div>
            <div><Label>CNPJ</Label><input style={{ ...campo, fontFamily: 'var(--mono, monospace)' }} value={cnpj} onChange={e => setCnpj(e.target.value)} /></div>
            <div><Label>Tipo</Label><select style={campo} value={tipo} onChange={e => setTipo(e.target.value)}><option value="">—</option><option value="A1">A1</option><option value="A3">A3</option><option value="e-CPF">e-CPF</option><option value="e-CNPJ">e-CNPJ</option>{tipo && !['A1', 'A3', 'e-CPF', 'e-CNPJ'].includes(tipo) && <option value={tipo}>{tipo}</option>}</select></div>
            <div><Label>Status</Label><input style={campo} value={status} onChange={e => setStatus(e.target.value)} placeholder="Válido" /></div>
            <div><Label>Vencimento</Label><input style={campo} type="date" value={vencimento || ''} onChange={e => setVencimento(e.target.value)} /></div>
            <div style={{ gridColumn: '1 / -1' }}><Label>Grupo de empresas</Label><input style={campo} value={grupo} onChange={e => setGrupo(e.target.value)} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Button size="sm" variant="accent" icon={salvandoDados ? undefined : 'check'} onClick={salvandoDados ? undefined : salvarDados}>{salvandoDados ? 'Salvando…' : 'Salvar dados'}</Button>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }} />
          {/* Contato */}
          <Secao>Contato do cliente</Secao>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><Label>E-mail</Label><input style={campo} value={email} onChange={e => setEmail(e.target.value)} placeholder="contato@cliente.com" /></div>
            <div><Label>Telefone</Label><input style={campo} value={telefone} onChange={e => setTelefone(e.target.value)} placeholder="(34) 90000-0000" /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Button size="sm" variant="default" icon={salvando ? undefined : 'check'} onClick={salvando ? undefined : salvarContato}>{salvando ? 'Salvando…' : 'Salvar contato'}</Button>
          </div>

          {/* Histórico de observações */}
          <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }} />
          <Secao>Histórico de observações</Secao>
          <textarea value={texto} onChange={e => setTexto(e.target.value)} placeholder="Nova observação · ex.: renovação solicitada, aguardando documento…" style={{ ...campo, minHeight: 56, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button size="sm" variant="accent" icon="plus" onClick={addObs}>Adicionar</Button>
          </div>
          <div style={{ marginTop: 8 }}>
            {lista === null ? <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>Carregando…</div>
              : lista.length === 0 ? <div style={{ color: 'var(--text-faint)', fontSize: 13, padding: '10px 0', textAlign: 'center' }}>Nenhuma observação ainda.</div>
              : lista.map(o => (
                <div key={o.id} style={{ display: 'flex', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--booker-orange)', flexShrink: 0, marginTop: 6 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--text-strong)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{o.texto}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>por <b style={{ color: 'var(--text)' }}>{o.por}</b> · {fmtDH(o.em)}</div>
                  </div>
                  <button onClick={() => delObs(o.id)} title="Excluir" style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--text-faint)', flexShrink: 0, cursor: 'pointer' }}><Icon name="trash" size={14} /></button>
                </div>
              ))}
          </div>
          {erro && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 10 }}>{erro}</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}

function Th({ children, right }) {
  return <th style={{ padding: '12px 16px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', textAlign: right ? 'right' : 'left' }}>{children}</th>;
}
function Kpi({ label, value, tone }) {
  return (
    <Card pad={16}>
      <div style={{ fontSize: 28, fontWeight: 800, color: tone, letterSpacing: '-.02em', lineHeight: 1 }} className="tnum">{value}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', marginTop: 6 }}>{label}</div>
    </Card>
  );
}

Object.assign(window, { Certificados });
